import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  apiService,
  ExportOptions,
  ExportPreview,
  ExportPropertySpec,
  ExportTemplate,
} from '../services/apiService';
import './Export.css';

type Message = { type: 'success' | 'error'; text: string } | null;

const BUILTIN_PREFIX = 'builtin:';
const CUSTOM_PREFIX = 'custom:';

const copyTemplate = (t: ExportTemplate): ExportTemplate => JSON.parse(JSON.stringify(t));

const formatUploadTime = (stamp: string | null): string => {
  if (!stamp) return '';
  const m = stamp.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]} UTC` : stamp;
};

interface PropertyRowsProps {
  rows: ExportPropertySpec[];
  sources: { value: string; label: string }[];
  dateFormats: { value: string; label: string }[];
  columns: string[];
  onChange: (rows: ExportPropertySpec[]) => void;
  caption: string;
}

/** Editable list of output properties: key ← source (+ the source's own settings). */
const PropertyRows: React.FC<PropertyRowsProps> = ({ rows, sources, dateFormats, columns, onChange, caption }) => {
  const update = (i: number, patch: Partial<ExportPropertySpec>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, d: number) => {
    const next = [...rows];
    const [item] = next.splice(i, 1);
    next.splice(i + d, 0, item);
    onChange(next);
  };
  const changeSource = (i: number, source: string) => {
    const base: ExportPropertySpec = { key: rows[i].key, source };
    if (source === 'column') base.column = columns[0] || '';
    if (source === 'text') base.value = '';
    if (source.startsWith('harvest')) base.date_format = 'iso';
    if (source === 'area' || source === 'area_computed') base.decimals = 2;
    onChange(rows.map((r, j) => (j === i ? base : r)));
  };

  return (
    <div className="table-container">
      <table className="export-props">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Property name in the file</th>
            <th scope="col">Value from</th>
            <th scope="col">Settings</th>
            <th scope="col">If empty, use</th>
            <th scope="col"><span className="visually-hidden">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>
                <input
                  type="text"
                  value={row.key}
                  aria-label={`Property ${i + 1} name`}
                  onChange={(e) => update(i, { key: e.target.value })}
                  placeholder="e.g. siteNumber"
                />
              </td>
              <td>
                <select
                  value={row.source}
                  aria-label={`Property ${i + 1} value source`}
                  onChange={(e) => changeSource(i, e.target.value)}
                >
                  {sources.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </td>
              <td>
                {row.source === 'column' && (
                  <>
                    <input
                      type="text"
                      list="export-columns"
                      value={row.column || ''}
                      aria-label={`Property ${i + 1} column`}
                      onChange={(e) => update(i, { column: e.target.value })}
                      placeholder="Column name"
                    />
                  </>
                )}
                {row.source === 'text' && (
                  <input
                    type="text"
                    value={row.value || ''}
                    aria-label={`Property ${i + 1} text`}
                    onChange={(e) => update(i, { value: e.target.value })}
                    placeholder="Text to write"
                  />
                )}
                {row.source.startsWith('harvest') && (
                  <select
                    value={row.date_format || 'iso'}
                    aria-label={`Property ${i + 1} date format`}
                    onChange={(e) => update(i, { date_format: e.target.value })}
                  >
                    {dateFormats.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                )}
                {(row.source === 'area' || row.source === 'area_computed') && (
                  <label className="inline-label">
                    Decimals{' '}
                    <input
                      type="number"
                      min={0}
                      max={8}
                      value={row.decimals ?? 4}
                      onChange={(e) => update(i, { decimals: Number(e.target.value) })}
                      className="narrow"
                    />
                  </label>
                )}
              </td>
              <td>
                {row.source !== 'text' && (
                  <input
                    type="text"
                    value={row.fallback || ''}
                    aria-label={`Property ${i + 1} value when empty`}
                    onChange={(e) => update(i, { fallback: e.target.value })}
                    placeholder="(leave empty)"
                  />
                )}
              </td>
              <td className="row-actions">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down">↓</button>
                <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className="btn btn-secondary add-row"
        onClick={() => onChange([...rows, { key: '', source: sources[0]?.value || 'plot_code' }])}
      >
        + Add property
      </button>
    </div>
  );
};

const Export: React.FC = () => {
  const [options, setOptions] = useState<ExportOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<Message>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selection, setSelection] = useState<string>('');
  const [draft, setDraft] = useState<ExportTemplate | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const previewSeq = useRef(0);

  const load = async (keepSelection?: string) => {
    try {
      const opts = await apiService.getExportOptions();
      setOptions(opts);
      const readable = opts.files.filter((f) => !f.error).map((f) => f.name);
      setSelectedFiles((prev) => (prev.length ? prev.filter((n) => readable.includes(n)) : readable));
      const first = keepSelection || `${BUILTIN_PREFIX}${opts.builtin_templates[0]?.id}`;
      choose(first, opts);
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to load export options' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findTemplate = (value: string, opts: ExportOptions | null = options): ExportTemplate | undefined => {
    if (!opts) return undefined;
    if (value.startsWith(BUILTIN_PREFIX)) {
      return opts.builtin_templates.find((t) => `${BUILTIN_PREFIX}${t.id}` === value);
    }
    return opts.templates.find((t) => `${CUSTOM_PREFIX}${t.id}` === value);
  };

  const choose = (value: string, opts: ExportOptions | null = options) => {
    const t = findTemplate(value, opts);
    setSelection(value);
    setDraft(t ? copyTemplate(t) : null);
    setEditing(false);
  };

  const isBuiltin = selection.startsWith(BUILTIN_PREFIX);

  const columns = useMemo(() => {
    const cols = new Set<string>();
    (options?.files || [])
      .filter((f) => selectedFiles.includes(f.name))
      .forEach((f) => (f.columns || []).forEach((c) => cols.add(c)));
    return Array.from(cols).sort();
  }, [options, selectedFiles]);

  // Preview whenever the template or the file choice changes (debounced while typing)
  useEffect(() => {
    if (!draft || selectedFiles.length === 0) {
      setPreview(null);
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await apiService.previewExport(draft, selectedFiles);
        if (seq === previewSeq.current) {
          setPreview(result);
          setPreviewError(null);
        }
      } catch (e) {
        if (seq === previewSeq.current) {
          setPreview(null);
          setPreviewError(e instanceof Error ? e.message : 'Preview failed');
        }
      } finally {
        if (seq === previewSeq.current) setPreviewLoading(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [draft, selectedFiles]);

  const toggleFile = (name: string) =>
    setSelectedFiles((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const startEdit = () => {
    if (!draft) return;
    if (isBuiltin) {
      // Built-in templates are read-only: edit a copy under a new name
      setDraft({ ...copyTemplate(draft), id: undefined, name: `${draft.name} (copy)` });
      setSelection('');
    }
    setEditing(true);
  };

  const startNew = () => {
    setSelection('');
    setDraft({
      name: 'New template',
      description: '',
      container: 'feature_collection',
      properties: [
        { key: 'plotCode', source: 'plot_code' },
        { key: 'harvestPeriod', source: 'harvest_period', date_format: 'iso' },
      ],
    });
    setEditing(true);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const saved = await apiService.saveExportTemplate(draft);
      setMessage({ type: 'success', text: `Template “${saved.name}” saved.` });
      await load(`${CUSTOM_PREFIX}${saved.id}`);
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    choose(selection || `${BUILTIN_PREFIX}${options?.builtin_templates[0]?.id}`);
  };

  const remove = async () => {
    if (!draft || typeof draft.id !== 'number') return;
    if (!window.confirm(`Delete the template “${draft.name}”?`)) return;
    try {
      await apiService.deleteExportTemplate(draft.id);
      setMessage({ type: 'success', text: `Template “${draft.name}” deleted.` });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to delete template' });
    }
  };

  const download = async () => {
    if (!draft || !options) return;
    const price = options.price_credits;
    const files = selectedFiles.length;
    if (price > 0 && !window.confirm(
      `Export ${files} file${files === 1 ? '' : 's'} with “${draft.name}” for ${price} credits?`
    )) return;
    setDownloading(true);
    setMessage(null);
    try {
      const result = await apiService.downloadExport(draft, selectedFiles);
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      const charged = result.creditsCharged ?? price;
      setMessage({
        type: 'success',
        text: `Downloaded ${result.filename}.` +
          (charged > 0 ? ` ${charged} credits charged` : '') +
          (result.creditsRemaining !== undefined && charged > 0 ? `; ${result.creditsRemaining} remaining.` : charged > 0 ? '.' : ''),
      });
    } catch (e) {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Export failed' });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="export-page">
        <div className="loading">
          <div className="spinner"></div>
          <span>Reading your uploaded files...</span>
        </div>
      </div>
    );
  }

  const collectionSources = (options?.sources || []).filter((s) => options?.collection_sources.includes(s.value));
  const hasFiles = (options?.files.length || 0) > 0;
  const price = options?.price_credits ?? 0;
  const totalWarnings = preview ? preview.files.reduce((n, f) => n + f.warnings.length, 0) : 0;

  return (
    <div className="export-page">
      <h1>Export to Customer JSON</h1>
      <p className="page-description">
        Turn your uploaded plot files into the GeoJSON format your customer asks for. Each uploaded file (e.g. one
        tanker) becomes one export file, with one feature per plot.
      </p>

      {message && (
        <div className={`export-message ${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}

      {!hasFiles ? (
        <div className="export-card">
          <h3>No uploaded files</h3>
          <p>Upload your plot files (Excel, CSV or GeoJSON) first, then come back to export them.</p>
          <a href="/upload" className="btn btn-primary">Go to Upload</a>
        </div>
      ) : (
        <>
          {/* 1. Files */}
          <section className="export-card" aria-labelledby="export-files-title">
            <h3 id="export-files-title">1. Files</h3>
            <p className="muted">From your latest upload ({formatUploadTime(options?.uploaded_at || null)}).</p>
            <ul className="export-files">
              {options?.files.map((f) => (
                <li key={f.name}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(f.name)}
                      disabled={!!f.error}
                      onChange={() => toggleFile(f.name)}
                    />{' '}
                    <span className="file-name">{f.name}</span>
                  </label>
                  {f.error ? (
                    <span className="file-error"> — could not be read: {f.error}</span>
                  ) : (
                    <span className="muted">
                      {' '}— {f.rows?.toLocaleString()} rows, {f.plots?.toLocaleString()} plots
                      {f.harvest_column ? `, harvest dates in “${f.harvest_column}”` : ', no harvest date column'}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {/* 2. Template */}
          <section className="export-card" aria-labelledby="export-template-title">
            <h3 id="export-template-title">2. Format</h3>
            {!editing ? (
              <>
                <div className="template-picker">
                  <label htmlFor="export-template">Template</label>
                  <select id="export-template" value={selection} onChange={(e) => choose(e.target.value)}>
                    <optgroup label="Built-in">
                      {options?.builtin_templates.map((t) => (
                        <option key={t.id} value={`${BUILTIN_PREFIX}${t.id}`}>{t.name}</option>
                      ))}
                    </optgroup>
                    {(options?.templates.length || 0) > 0 && (
                      <optgroup label="My templates">
                        {options?.templates.map((t) => (
                          <option key={t.id} value={`${CUSTOM_PREFIX}${t.id}`}>{t.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <div className="template-actions">
                    <button type="button" className="btn btn-secondary" onClick={startEdit} disabled={!draft}>
                      {isBuiltin ? 'Copy and edit' : 'Edit'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={startNew}>New template</button>
                    {!isBuiltin && draft && (
                      <button type="button" className="btn btn-secondary danger" onClick={remove}>Delete</button>
                    )}
                  </div>
                </div>
                {draft?.description && <p className="muted">{draft.description}</p>}
                {draft && (
                  <table className="template-summary">
                    <caption className="visually-hidden">Properties written for each plot</caption>
                    <thead>
                      <tr>
                        <th scope="col">Property</th>
                        <th scope="col">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.properties.map((p) => (
                        <tr key={p.key}>
                          <td className="mono">{p.key}</td>
                          <td>
                            {options?.sources.find((s) => s.value === p.source)?.label || p.source}
                            {p.source === 'column' && `: “${p.column}”`}
                            {p.source === 'text' && `: “${p.value}”`}
                            {p.date_format && ` (${options?.date_formats.find((f) => f.value === p.date_format)?.label})`}
                            {p.fallback && ` — if empty: “${p.fallback}”`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            ) : draft && (
              <div className="template-editor">
                <div className="editor-grid">
                  <label>
                    Template name
                    <input type="text" value={draft.name} maxLength={100}
                           onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </label>
                  <label>
                    File layout
                    <select value={draft.container} onChange={(e) => setDraft({ ...draft, container: e.target.value })}>
                      {options?.containers.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="span-2">
                    Description (optional)
                    <input type="text" value={draft.description || ''} maxLength={500}
                           onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                           placeholder="e.g. Format requested by Rubber Leisure, Oct 2026" />
                  </label>
                </div>

                <h4>Properties of each plot</h4>
                <PropertyRows
                  rows={draft.properties}
                  sources={options?.sources || []}
                  dateFormats={options?.date_formats || []}
                  columns={columns}
                  caption="Properties written for each plot"
                  onChange={(rows) => setDraft({ ...draft, properties: rows })}
                />

                {draft.container === 'producer_collections' && (
                  <>
                    <h4>Properties of each producer collection</h4>
                    <PropertyRows
                      rows={draft.collection_properties || []}
                      sources={collectionSources}
                      dateFormats={options?.date_formats || []}
                      columns={columns}
                      caption="Properties written for each producer collection"
                      onChange={(rows) => setDraft({ ...draft, collection_properties: rows })}
                    />
                  </>
                )}

                <datalist id="export-columns">
                  {columns.map((c) => <option key={c} value={c} />)}
                </datalist>

                <div className="template-actions">
                  <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                    {saving ? 'Saving...' : 'Save template'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
                </div>
              </div>
            )}
          </section>

          {/* 3. Preview and download */}
          <section className="export-card" aria-labelledby="export-preview-title">
            <h3 id="export-preview-title">3. Preview and download</h3>
            {selectedFiles.length === 0 ? (
              <p className="muted">Select at least one file.</p>
            ) : previewError ? (
              <div className="export-message error" role="alert">{previewError}</div>
            ) : !preview ? (
              <p className="muted">{previewLoading ? 'Building preview...' : ''}</p>
            ) : (
              <>
                <div className="table-container">
                  <table className="export-summary">
                    <caption className="visually-hidden">Files that will be exported</caption>
                    <thead>
                      <tr>
                        <th scope="col">Uploaded file</th>
                        <th scope="col">Export file</th>
                        <th scope="col">Rows</th>
                        <th scope="col">Features (plots)</th>
                        <th scope="col">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.files.map((f) => (
                        <tr key={f.name}>
                          <td>{f.name}</td>
                          <td className="mono">{f.output_name}</td>
                          <td>{f.rows.toLocaleString()}</td>
                          <td>{f.features.toLocaleString()}</td>
                          <td>
                            {f.warnings.length === 0 ? (
                              <span className="risk-low">OK</span>
                            ) : (
                              <ul className="warnings">
                                {f.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="muted">
                  Sample from {preview.sample_file} (first plots only){previewLoading ? ' — updating...' : ''}:
                </p>
                <pre className="json-sample" tabIndex={0}>{JSON.stringify(preview.sample, null, 2)}</pre>
              </>
            )}

            <div className="download-row">
              <button
                type="button"
                className="btn btn-primary"
                onClick={download}
                disabled={downloading || editing || !draft || selectedFiles.length === 0 || !!previewError}
              >
                {downloading
                  ? 'Exporting...'
                  : `Download ${selectedFiles.length > 1 ? `${selectedFiles.length} files (zip)` : 'file'}` +
                    (price > 0 ? ` — ${price} credits` : ' — free')}
              </button>
              {editing && <span className="muted">Save or cancel your template changes first.</span>}
              {!editing && totalWarnings > 0 && <span className="muted">Check the notes above before downloading.</span>}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Export;
